import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de reservas para el panel de staff (Fase 1, slice 2). Habla con
 * Supabase SOLO a través del cliente anon de navegador (supabase.client.js);
 * el acceso lo enforcea RLS (`reservations_select_staff_or_own` /
 * `reservations_admin_write`). Éste es el ÚNICO módulo que emite queries
 * Supabase para reservas de admin — el service y los componentes no conocen
 * el SDK.
 */

/**
 * Reservas ACTIVAS con `fecha >= from`, ordenadas por fecha y luego hora. Las
 * archivadas quedan EXCLUIDAS (`.neq('status','archivada')`): pertenecen a la
 * vista de archivadas (`fetchArchived`), nunca a la lista principal
 * (Req: Archived Reservations View).
 *
 * Si Supabase no está configurado devuelve `[]` — mismo tratamiento que "RLS
 * sin filas" (Req: Staff-Only Read Access): la UI no debe distinguir
 * "no configurado" de "sin datos", solo el estado vacío estándar.
 * @param {{ from: string }} params fecha ISO (YYYY-MM-DD), límite inferior inclusive
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function fetchUpcoming({ from }) {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('reservations')
        // Las mesas asignadas viajan con la reserva (join anidado) en vez de en
        // una segunda consulta: la fila de la lista las muestra siempre, así que
        // pedirlas aparte sería un round-trip garantizado por cada render.
        .select('*, reservation_tables(mesa_id, mesas(numero))')
        .gte('fecha', from)
        .neq('status', 'archivada')
        .order('fecha', { ascending: true })
        .order('hora', { ascending: true });

    if (error) throw new Error(error.message);
    return data ?? [];
}

/**
 * Reservas ARCHIVADAS como lista plana, ordenadas por fecha DESCENDENTE (las
 * más recientes primero — Req: Archived Reservations View). No se filtra por
 * `from`: la vista de archivadas muestra todo el histórico archivado.
 *
 * Si Supabase no está configurado devuelve `[]` (mismo criterio de
 * degradación que `fetchUpcoming`).
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function fetchArchived() {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('reservations')
        // Las mesas asignadas viajan con la reserva (join anidado) en vez de en
        // una segunda consulta: la fila de la lista las muestra siempre, así que
        // pedirlas aparte sería un round-trip garantizado por cada render.
        .select('*, reservation_tables(mesa_id, mesas(numero))')
        .eq('status', 'archivada')
        .order('fecha', { ascending: false });

    if (error) throw new Error(error.message);
    return data ?? [];
}

/**
 * Capacidad (en personas) por área, como mapa `{ [area]: capacidad }`.
 *
 * Devuelve `{}` si Supabase no está configurado (mismo criterio de
 * degradación que `fetchUpcoming`).
 * @returns {Promise<Record<string, number>>}
 * @throws {Error} si Supabase responde con error
 */
export async function fetchAreas() {
    const client = getSupabaseClient();
    if (!client) return {};

    const { data, error } = await client.from('areas').select('name, capacity');
    if (error) throw new Error(error.message);

    return (data ?? []).reduce((map, row) => {
        map[row.name] = row.capacity;
        return map;
    }, {});
}

/**
 * @typedef {'confirmada' | 'terminada' | 'cancelada' | 'archivada'} ReservationStatus
 * @typedef {{ to: ReservationStatus, from: ReservationStatus[] }} ReservationTransition
 */

/**
 * Actualiza el `status` de una reserva de forma CONDICIONAL: solo escribe si
 * la fila sigue en uno de los estados `from` (allowed source) en el momento
 * del UPDATE — evita pisar un cambio concurrente hecho por otra sesión de
 * staff, o aplicar una transición fuera del state machine (Req: Staff Status
 * Transition Actions / Archive Action Restricted to Cancelada or Terminada,
 * escenario "Concurrent transition is rejected").
 *
 * El caller DEBE interpretar `affected: false` como "la reserva ya no estaba
 * en un estado `from` válido" (0 filas afectadas — ya cambió de estado
 * concurrentemente, o la transición no era legal), no como un error — y
 * re-consultar el estado actual de la fila.
 * @param {string} id
 * @param {ReservationTransition} transition target status (`to`) and allowed source statuses (`from`)
 * @returns {Promise<{ affected: boolean }>}
 * @throws {Error} si Supabase no está configurado o la query falla
 */
export async function updateStatus(id, { to, from }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Actualización no disponible');

    const { data, error } = await client
        .from('reservations')
        .update({ status: to })
        .eq('id', id)
        .in('status', from)
        .select();

    if (error) throw new Error(error.message);
    return { affected: (data ?? []).length > 0 };
}

/**
 * Edita los campos de una reserva (todo menos `status`, que tiene su propio
 * camino condicional en `updateStatus`). El acceso lo enforcea RLS
 * (`reservations_admin_write`, UPDATE con `is_staff()`).
 *
 * `status` se excluye a propósito aunque venga en `fields`: el state machine
 * de estados vive en `updateStatus` y dejarlo entrar por acá permitiría saltear
 * el chequeo `from` y pisar una transición concurrente.
 *
 * Mover una reserva de fecha/momento arrastra sus mesas asignadas (trigger
 * `reservations_propagate_to_tables`), y si en el destino alguna de esas mesas
 * ya está tomada el UPDATE ENTERO falla con `23505`. El código se preserva para
 * que el service lo pueda traducir.
 *
 * @param {string} id
 * @param {{ nombre?: string, apellido?: string, telefono?: string|null, fecha?: string, momento?: string, hora?: string|null, personas?: number, area?: string }} fields
 * @returns {Promise<object>} la fila actualizada
 * @throws {Error} con `code` de Postgres cuando lo hay
 */
export async function updateReservation(id, fields) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Edición no disponible');

    const { status, id: _ignoredId, ...editables } = fields;

    const { data, error } = await client
        .from('reservations')
        .update(editables)
        .eq('id', id)
        .select()
        .single();

    if (error) {
        const err = new Error(error.message);
        if (error.code) err.code = error.code;
        throw err;
    }
    return data;
}
